import { isWebShareSupported } from '@georapbox/web-share-element/dist/is-web-share-supported.js';
import { getSettings } from '../services/storage.js';
import { NO_BARCODE_DETECTED } from '../constants.js';

const styles = /* css */ `
  :host {
    box-sizing: border-box;
  }

  :host *,
  :host *::before,
  :host *::after {
    box-sizing: inherit;
  }

  :host([hidden]),
  [hidden],
  ::slotted([hidden]) {
    display: none !important;
  }

.error-result .result__item {
  color: var(--error-color);
  font-weight: bold;
}

  .result {
    position: relative;
    width: 100%;
    border: 1px solid var(--focus);
    border-radius: 6px;
    margin: 1.5rem 0 0 0;
    padding: 1rem;
    background-color: var(--background-alt);
    text-align: center;
  }

  .result__item {
    word-wrap: break-word;
    word-break: break-word;
  }

  a.result__item {
    color: var(--links);
  }

  .result__item--no-barcode {
    color: var(--error-color);
  }

  .product-not-found {
    color: var(--error-color);
    font-weight: bold;
  }

  .result__actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-top: 1rem;
  }



  .result web-share button,
  .result custom-clipboard-copy::part(button) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    margin: 0;
    padding: 0.5rem 0.75rem;
    background-color: transparent;
    border: 1px solid var(--border);
    border-radius: var(--border-radius);
    color: inherit;
    line-height: 1;
    font-family: inherit;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  .result web-share button:hover,
  .result custom-clipboard-copy::part(button):hover {
    background-color: var(--background);
  }
`;

const template = document.createElement('template');

template.innerHTML = /* html */ `
  <style>${styles}</style>

  <div class="result">
    <div class="result__item"></div>
    <div class="result__actions">
      <custom-clipboard-copy hidden></custom-clipboard-copy>
      <web-share hidden>
        <button slot="button" type="button">
          <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M13.5 1a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zM11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5zm-8.5 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3zm11 5.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3z"/>
          </svg>
          Partajează
        </button>
      </web-share>
    </div>
  </div>
`;

class BSResult extends HTMLElement {
  constructor() {
    super();

    if (!this.shadowRoot) {
      const shadowRoot = this.attachShadow({ mode: 'open' });
      shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  get value() {
    return this.getAttribute('value');
  }

  set value(value) {
    this.setAttribute('value', value);
  }

  static get observedAttributes() {
    return ['value'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'value' && oldValue !== newValue) {
      this.#handleValueChange(this.value);
    }
  }

  connectedCallback() {
    this.#upgradeProperty('value');

    if (!isWebShareSupported()) {
      const webShareEl = this.shadowRoot.querySelector('web-share');
      if (webShareEl) {
        webShareEl.hidden = true;
      }
    }
  }

  #formatProductData(productData) {
    if (productData.articol === "Produs inexistent") {
      return `Cod de bare: ${productData.codbar}\nProdus inexistent`;
    }
    
    return `Cod de bare: ${productData.codbar}
Denumire Produs: ${productData.articol}
Preț de vânzare: ${productData.pv} RON
Stoc: ${productData.stoc}`;
  }

  async #handleValueChange(value) {
    const baseEl = this.shadowRoot.querySelector('.result');
    const resultItemEl = baseEl.querySelector('.result__item');
    const resultActionsEl = baseEl.querySelector('.result__actions');
    const clipboardCopyEl = baseEl.querySelector('custom-clipboard-copy');
    const webShareEl = baseEl.querySelector('web-share');

    // Clear previous content
    resultItemEl.innerHTML = '';

    try {
      const [, settings] = await getSettings();
      const data = JSON.parse(value);
      const productData = data[0]; // Get the first item from the array
      
      // Format the product data for display, copy and share
      const formattedText = this.#formatProductData(productData);
      
      if (productData.articol === "Produs inexistent") {
        const codbarP = document.createElement('p');
        codbarP.textContent = `Cod de bare: ${productData.codbar}`;
        
        const notFoundElement = document.createElement('p');
        notFoundElement.className = 'product-not-found';
        notFoundElement.textContent = productData.articol;
        
        resultItemEl.appendChild(codbarP);
        resultItemEl.appendChild(notFoundElement);
		
      } else {
        // Create elements for each data field
        const codbarP = document.createElement('p');
        codbarP.textContent = `Cod de bare: ${productData.codbar}`;
        
        const articolP = document.createElement('p');
        articolP.textContent = `Denumire Produs: ${productData.articol}`;
        
        const pvP = document.createElement('p');
        pvP.textContent = `Preț de vânzare: ${productData.pv} RON`;
        
        const stocP = document.createElement('p');
        stocP.textContent = `Stoc: ${productData.stoc}`;
        
        // Append all elements to the result item
        resultItemEl.appendChild(codbarP);
        resultItemEl.appendChild(articolP);
        resultItemEl.appendChild(pvP);
        resultItemEl.appendChild(stocP);
	
	
        // Handle URL case if needed
        try {
          new URL(productData.codbar);
          const linkItem = document.createElement('a');
          linkItem.href = productData.codbar;
          linkItem.textContent = 'Open Barcode Link';
          if (!settings?.openWebPageSameTab) {
            linkItem.setAttribute('target', '_blank');
            linkItem.setAttribute('rel', 'noreferrer noopener');
          }
          if (settings?.openWebPage) {
            linkItem.click();
          }
          resultItemEl.appendChild(linkItem);
        } catch {
          // Not a URL, do nothing
        }
      }

      // Set up clipboard copy and share with formatted text
	  
	 
	let formattedTextCSV = '';
if (productData.articol === "Produs inexistent") {
  formattedTextCSV = `${productData.codbar}, ${productData.articol}`;
} else {
  formattedTextCSV = `${productData.codbar}, ${productData.articol}, ${productData.pv} RON, Stoc: ${productData.stoc}`;
}



      const isValidValue = value !== NO_BARCODE_DETECTED;
      
      if (clipboardCopyEl && isValidValue) {
        clipboardCopyEl.setAttribute('value', formattedTextCSV);
        clipboardCopyEl.hidden = false;
      } else {
        clipboardCopyEl.hidden = true;
        clipboardCopyEl.removeAttribute('value');
      }

      if (webShareEl && isWebShareSupported() && isValidValue) {
        webShareEl.setAttribute('share-text', formattedTextCSV);
        webShareEl.hidden = false;
      } else {
        webShareEl.hidden = true;
        webShareEl.removeAttribute('share-text');
      }
    } catch (e) {
      // Fallback for non-JSON values or errors
      resultItemEl.textContent = value;
      resultItemEl.classList.toggle('result__item--no-barcode', value === NO_BARCODE_DETECTED);
      
      // Hide actions for invalid values
      clipboardCopyEl.hidden = true;
      webShareEl.hidden = true;
    }
  }

  /**
   * This is to safe guard against cases where, for instance, a framework may have added the element to the page and
   * set a value on one of its properties, but lazy loaded its definition. Without this guard, the upgraded element would
   * miss that property and the instance property would prevent the class property setter from ever being called.
   *
   * https://developers.google.com/web/fundamentals/web-components/best-practices#lazy-properties
   *
   * @param {string} prop - The property to upgrade.
   */
  #upgradeProperty(prop) {
    const instance = this;
    if (Object.prototype.hasOwnProperty.call(instance, prop)) {
      const value = instance[prop];
      delete instance[prop];
      instance[prop] = value;
    }
  }

  static defineCustomElement(elementName = 'bs-result') {
    if (typeof window !== 'undefined' && !window.customElements.get(elementName)) {
      window.customElements.define(elementName, BSResult);
    }
  }
}

BSResult.defineCustomElement();